// react
import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';

// components
import { BackButton } from '@/components/molecules';
import { CardContainer } from '@/components/templates';
import { Button, Card, ProgressBar, Title } from '@/components/atoms';
import { EligibilityCalculator } from '@/components/organisms';

/**
 * RecipientProfileAttendanceAllowance shows the profile of the care recipient.
 */
export const RecipientProfileAttendanceAllowance = ({
  progress,
  handleStart,
  limit,
  fields,
  answers,
  setAnswers,
  onUpdateCareProfile,
  onEligibilityStatusChange,
}: RecipientProfileAttendanceAllowanceProps) => {
  // memos
  const isComplete = useMemo(() => {
    if (progress === undefined || limit === undefined) return false;
    return progress >= limit;
  }, [progress, limit]);

  const WeMakeClaimingEasy = memo(() => (
    <StyledCard color='green'>
      <CareProfileCardBody>
        <CardContent>
          <CardTitle>We make claiming easy:</CardTitle>
          <CardBody>
            <ol>
              <li>Complete the form below with 24/7 AI chatbot support.</li>
              <li>
                We’ll print your information onto{' '}
                <Button
                  ariaLabel='view gov form'
                  variant='link'
                  isInline
                  to={
                    'https://assets.publishing.service.gov.uk/media/65e5b1e63f694514a3036001/aa1-interactive-claim-form.pdf'
                  }
                >
                  this government form
                </Button>
                , and post it to you for signing.
              </li>
              <li>
                Post the form to the government using our free post envelope
                provided.
              </li>
            </ol>
          </CardBody>
        </CardContent>
      </CareProfileCardBody>
    </StyledCard>
  ));

  const ProgressCard = memo(() => (
    <StyledCard color={isComplete ? 'blue' : 'green'}>
      <CareProfileCardWrapper>
        <CareProfileCardBody>
          <CardContent>
            <CardTitle>
              {isComplete
                ? "Well done! You've completed the attendance allowance"
                : "You're doing great!"}
            </CardTitle>
            <CardBody>
              {isComplete
                ? 'You can now book a call back to discuss your attendance allowance form.'
                : 'Continue completing your attendance allowance application that could give you up to £5.6k a year to cover care costs'}
            </CardBody>
          </CardContent>
          <ProgressBar
            label='Progress'
            limit={limit || 0}
            progress={progress}
          />
          <ActionGroup>
            <Button
              ariaLabel={
                isComplete ? 'view attendance allowance' : 'continue claim'
              }
              variant={isComplete ? 'secondary' : 'primary'}
              onPress={handleStart}
              isFullWidth
            >
              {isComplete ? 'View claim' : 'Continue claim'}
            </Button>
          </ActionGroup>
        </CareProfileCardBody>
      </CareProfileCardWrapper>
    </StyledCard>
  ));

  const isStarted = useMemo(
    () => progress !== undefined && progress !== 0,
    [progress],
  );

  return (
    <Body $isStarted={isStarted}>
      <BackButton />
      <Title>Claim Attendance Allowance</Title>
      <HelperText>
        The government benefit Attendance Allowance could cover up to £5.6k of
        your care costs per year.
      </HelperText>

      <CardContainer maxColumns={1} isPreventHeightMatch>
        {/* progress card */}
        {isStarted && <ProgressCard />}

        {/* eligibility calculator */}
        <Card isNoPadding>
          <WrapperEligibilityCalculator>
            <EligibilityCalculator
              fields={fields}
              answers={answers}
              setAnswers={setAnswers}
              onEligibilityStatusChange={onEligibilityStatusChange}
              onUpdateCareProfile={onUpdateCareProfile}
            />
            <Banner>Eligibility calculator</Banner>
          </WrapperEligibilityCalculator>
        </Card>

        {!isStarted && (
          <>
            {/* we make claiming easy */}
            <WeMakeClaimingEasy />

            {/* start now button */}
            <StartNowWrapper>
              <StyledButton
                ariaLabel='start now'
                variant='primary'
                to='/attendance-allowance/attendance-allowance-general-information'
              >
                Start Now
              </StyledButton>
            </StartNowWrapper>
          </>
        )}
      </CardContainer>
    </Body>
  );
};

interface BodyProps {
  $isStarted?: boolean;
}
const Body = styled.div<BodyProps>`
  position: relative;
  padding-bottom: ${(props) => props.theme.spacing[96]};
  ${(props) =>
    props.$isStarted &&
    css`
      padding-bottom: ${props.theme.spacing[24]};
    `}
`;

const HelperText = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  padding-bottom: ${(props) => props.theme.spacing[36]};
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[32]};
  align-items: flex-start;
  color: ${(props) => props.theme.color.neutral[80]};
  border-radius: 8px;
`;

const CareProfileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing[32]};
  flex: 1;
`;

const CareProfileCardWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};
`;

const CardTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const CardBody = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};

  & > ul {
    margin-bottom: 0;
  }
`;

const ActionGroup = styled.div`
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;
  max-width: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Banner = styled.div`
  background: ${(props) => props.theme.color.primary[80]};
  color: ${(props) => props.theme.color.neutral[10]};
  font: ${(props) => props.theme.typography.body.xxs.medium};
  padding: ${(props) => props.theme.spacing[4]}
    ${(props) => props.theme.spacing[8]};
  position: absolute;
  top: 0;
  left: 3%;
  transform: translateX(-3%);
  border-radius: 0 0 6px 6px;
`;

const WrapperEligibilityCalculator = styled.div`
  position: relative;
  padding-top: ${(props) => props.theme.spacing[2]};
`;

const StartNowWrapper = styled.div`
  position: fixed;
  bottom: 67px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  background: ${(props) => props.theme.color.neutral[10]};
  border-top: 1px solid ${(props) => props.theme.color.neutral[50]};
  padding: ${(props) => props.theme.spacing[16]};

  ${(props) =>
    props.theme.breakpoint.sm(css`
      bottom: 0;
    `)}
`;

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 350px;
`;

interface RecipientProfileAttendanceAllowanceProps {
  limit?: number;
  progress?: number;
  handleStart: () => void;
  fields: any;
  answers: { [key: string]: string };
  setAnswers: (answers: any) => void;
  onUpdateCareProfile: () => Promise<boolean>;
  onEligibilityStatusChange?: (status: string) => void;
}

RecipientProfileAttendanceAllowance.displayName =
  'RecipientProfileAttendanceAllowance';

export default RecipientProfileAttendanceAllowance;
